'use client'

// Pick a profile picture from the wallet's image inscriptions. Built for the
// wallet that holds thousands: search by name, collection or number, narrow
// to one collection, and the grid grows as you scroll instead of mounting
// every image at once.

import React from 'react'
import { Check, ImageOff, Search, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Inscription } from '@/utils/types/inscription'
import { resolveTwetchImage } from '../lib/twetch-media'

const STEP = 40
/// Collections offered as one-tap filters, most-held first.
const CHIP_LIMIT = 12

interface PickerItem {
	id: string
	num: number | null
	name: string
	collection: string | null
	haystack: string
}

function toItem(inscription: Inscription): PickerItem {
	const raw = inscription as unknown as {
		id: string
		num?: number | null
		meta?: { name?: string | null } | null
		collection?: { name?: string | null } | null
	}
	const collection = raw.collection?.name?.trim() || null
	const name =
		raw.meta?.name?.trim() || (raw.num != null ? `Inscription #${raw.num}` : 'Inscription')
	return {
		id: raw.id,
		num: raw.num ?? null,
		name,
		collection,
		haystack: `${name} ${collection ?? ''} ${raw.num ?? ''} ${raw.id}`.toLowerCase()
	}
}

export default function InscriptionPicker({
	inscriptions,
	loading,
	value,
	onChange
}: {
	inscriptions: Inscription[] | undefined
	loading: boolean
	/// The picked `ord://<id>` ref, or null.
	value: string | null
	onChange: (ref: string | null) => void
}) {
	const [query, setQuery] = React.useState('')
	const [collection, setCollection] = React.useState<string | null>(null)
	const [shown, setShown] = React.useState(STEP)
	const sentinelRef = React.useRef<HTMLDivElement | null>(null)

	const items = React.useMemo(
		() =>
			(inscriptions ?? []).filter((i) => (i.content_type ?? '').startsWith('image/')).map(toItem),
		[inscriptions]
	)
	const collections = React.useMemo(() => {
		const counts = new Map<string, number>()
		for (const item of items) {
			if (item.collection) counts.set(item.collection, (counts.get(item.collection) ?? 0) + 1)
		}
		return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, CHIP_LIMIT)
	}, [items])

	const needle = query.trim().toLowerCase()
	const matches = React.useMemo(
		() =>
			items.filter(
				(item) =>
					(collection === null || item.collection === collection) &&
					(needle === '' || item.haystack.includes(needle))
			),
		[items, collection, needle]
	)

	// A new filter starts the grid over (adjusted during render).
	const filterKey = `${collection ?? ''}|${needle}`
	const [lastFilter, setLastFilter] = React.useState(filterKey)
	if (lastFilter !== filterKey) {
		setLastFilter(filterKey)
		setShown(STEP)
	}

	const hasMore = shown < matches.length
	React.useEffect(() => {
		const el = sentinelRef.current
		if (!el || !hasMore) return
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setShown((n) => n + STEP)
				}
			},
			{ rootMargin: '240px 0px' }
		)
		observer.observe(el)
		return () => observer.disconnect()
	}, [hasMore, shown])

	if (loading) {
		return (
			<div className="grid grid-cols-4 gap-2 sm:grid-cols-5" aria-hidden="true">
				{Array.from({ length: 10 }, (_, i) => (
					<span key={i} className="aspect-square animate-pulse rounded-xl bg-secondary/60" />
				))}
			</div>
		)
	}
	if (items.length === 0) {
		return (
			<div className="flex items-center gap-3 rounded-xl bg-secondary/40 px-3.5 py-4">
				<ImageOff className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
				<p className="text-xs leading-relaxed text-muted-foreground">
					No image inscriptions in this wallet. Hold one and it becomes a picture option here.
				</p>
			</div>
		)
	}

	// A handful needs no tools; they only appear once the grid outgrows a glance.
	const searchable = items.length > STEP / 2
	return (
		<div data-testid="alkanes-twetch-profile-inscriptions">
			{searchable && (
				// Pinned while the grid scrolls under it, so the filter is always
				// in reach however deep the wallet goes.
				<div className="sticky top-0 z-10 -mx-5 bg-card px-5 pb-2.5 pt-2 before:absolute before:inset-x-0 before:-top-5 before:h-5 before:bg-card">
					<label className="relative block">
						<span className="sr-only">Search your inscriptions</span>
						<Search
							className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
							aria-hidden="true"
						/>
						<input
							data-testid="inscription-picker-search"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder={`Search ${items.length.toLocaleString(
								'en-US'
							)} images by name, collection or number`}
							autoComplete="off"
							spellCheck={false}
							className="h-10 w-full rounded-xl bg-secondary/60 pl-9 pr-9 text-base font-normal text-foreground outline-none ring-1 ring-transparent placeholder:text-muted-foreground focus-visible:ring-primary sm:text-sm"
						/>
						{query && (
							<button
								type="button"
								aria-label="Clear search"
								onClick={() => setQuery('')}
								className="absolute right-1.5 top-1/2 grid size-7 -translate-y-1/2 place-items-center rounded-lg text-muted-foreground hover:text-foreground"
							>
								<X className="size-4" aria-hidden="true" />
							</button>
						)}
					</label>
					{collections.length > 1 && (
						<div
							role="group"
							aria-label="Filter by collection"
							className="-mx-5 mt-2 flex gap-1.5 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
						>
							{[[null, items.length] as const, ...collections].map(([name, count]) => {
								const on = collection === name
								return (
									<button
										key={name ?? '__all'}
										type="button"
										aria-pressed={on}
										title={name ?? undefined}
										onClick={() => setCollection(on ? null : name)}
										className={cn(
											'shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors',
											on
												? 'bg-primary text-primary-foreground'
												: 'bg-secondary/60 text-muted-foreground hover:text-foreground'
										)}
									>
										<span className="inline-block max-w-[9rem] truncate align-bottom">
											{name ?? 'All'}
										</span>{' '}
										<span
											className={cn('tabular-nums font-normal', on ? 'opacity-80' : 'opacity-70')}
										>
											{count.toLocaleString('en-US')}
										</span>
									</button>
								)
							})}
						</div>
					)}
				</div>
			)}

			{matches.length === 0 ? (
				<p className="rounded-xl bg-secondary/40 px-3.5 py-5 text-center text-xs text-muted-foreground">
					Nothing matches “{query.trim()}”{collection ? ` in ${collection}` : ''}.
				</p>
			) : (
				<div className="-m-1 grid grid-cols-4 gap-2 p-1 sm:grid-cols-5">
					{matches.slice(0, shown).map((item) => {
						const ref = `ord://${item.id}`
						const selected = value === ref
						return (
							<button
								key={item.id}
								type="button"
								aria-pressed={selected}
								aria-label={item.name}
								title={item.collection ? `${item.name} · ${item.collection}` : item.name}
								onClick={() => onChange(selected ? null : ref)}
								className={cn(
									'relative aspect-square overflow-hidden rounded-xl bg-secondary/60 outline-none ring-2 ring-offset-2 ring-offset-card transition-shadow focus-visible:ring-primary/60',
									selected ? 'ring-primary' : 'ring-transparent'
								)}
							>
								<img
									src={resolveTwetchImage(ref) ?? undefined}
									alt=""
									loading="lazy"
									decoding="async"
									className="size-full object-cover [image-rendering:pixelated]"
								/>
								{selected && (
									<span className="absolute right-1 top-1 grid size-5 place-items-center rounded-full bg-primary text-primary-foreground">
										<Check className="size-3" strokeWidth={3} aria-hidden="true" />
									</span>
								)}
							</button>
						)
					})}
				</div>
			)}
			{hasMore && (
				// Grows on scroll; the button is the same action for keyboards and
				// for browsers that hold observers back (background tabs).
				<div ref={sentinelRef} className="pt-3 text-center">
					<button
						type="button"
						data-testid="inscription-picker-more"
						onClick={() => setShown((n) => n + STEP)}
						className="rounded-full bg-secondary/60 px-4 py-2 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
					>
						Show more · {(matches.length - shown).toLocaleString('en-US')} left
					</button>
				</div>
			)}
		</div>
	)
}
